import { GrainLoader } from "@/components/ui/GrainLoader";
import {
  ArrowRight,
  Headset,
  Book,
  CircleXIcon,
  RotateCcwIcon,
  HeadsetIcon,
} from "lucide-react";
import "../styles/gradients.css";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "./ui/button";
import { useWarehouse } from "@/hooks/useWarehouse";
import { StarShape, Pulse, SarasIQ } from "./icons";
import { ProgressContainer } from "./ProgressContainer";
import { motion } from "framer-motion";
import { UserAvatar } from "./UserAvatar";
import { Card } from "./ui/card";
import { LINKS } from "@/constants";

export const WarehouseScreen = ({ className }: { className?: string }) => {
  const { userDetails, currentUser, warehouseLoading, warehouseError } =
    useAuth();

  const {
    refetchWarehouse,
    createWarehouse,
    isCreatingWarehouse,
    fetchEnabledWarehouses,
    isEnabledWarehousesLoading,
  } = useWarehouse(userDetails, currentUser);

  return (
    <ProgressContainer progressValue={100} className={`${className || ''} relative w-full iq-warehouse-container`}>
      {/* Progress Bar Header */}
      <div className="block md:flex items-center justify-center flex-col">
        {warehouseLoading ?
          <>
            <GrainLoader />
            <div className="text-[color:var(--dash-muted,var(--text-medium-emphasis))] mt-6">
              Connecting pulse backend...
            </div>
          </>
        : warehouseError ?
          <>
            <div className="flex items-center justify-center md:inline">
              <CircleXIcon size={62} className="text-[color:var(--dash-neg,var(--semantic-error-500))]" />
            </div>
            <div className="text-[color:var(--dash-muted,var(--text-medium-emphasis))] mt-6">
              Oops! there was an issue while connecting to your Pulse backend.
              Please try again or come back later
            </div>
            <div className="mt-4">
              <Button
                variant="default"
                className="bg-[var(--dash-accent,var(--primary-500))] text-[color:var(--dash-band-active-ink,var(--structural-white))] hover:brightness-95 cursor-pointer"
                onClick={() => {
                  refetchWarehouse();
                }}
              >
                <RotateCcwIcon className="" />
                Retry Connecting
              </Button>
            </div>
            <div className="mt-4">
              <Button
                variant="link"
                className="text-[color:var(--dash-accent,var(--primary-500))] hover:brightness-95 cursor-pointer"
                onClick={() => {}}
              >
                <HeadsetIcon />
                Contact Support
              </Button>
            </div>
          </>
        : <>
            <div className="flex flex-col items-center justify-center gap-7">
              <div className="flex flex-col items-center justify-center gap-7.5">
                <div className="flex flex-col items-center justify-center gap-6">
                  <div className="flex items-center justify-center block h-auto gap-6">
                    <div className="flex flex-col items-center gap-4">
                      <Pulse size={80} className="w-20 h-16 text-[color:var(--dash-accent,var(--primary-600))] hidden md:inline" />
                      <Pulse size={40} className="w-10 h-8 text-[color:var(--dash-accent,var(--primary-600))] md:hidden" />
                      <span className="font-sourcesans italic text-[color:var(--dash-muted,var(--text-medium-emphasis))]">
                        Pulse
                      </span>
                    </div>

                    <div className="-mt-10">
                      <div className="w-[100px] bg-[var(--dash-surface-2,var(--structural-neutral-2))] h-[6px] rounded-full flex overflow-hidden">
                        <motion.div
                          className="w-[65px] h-[6px] rounded-full flex justify-between"
                          // GPU-composited via the `x` transform shorthand. Kept as a
                          // shorthand (not a raw `transform` string) so the app-root
                          // <MotionConfig reducedMotion="user"> can neutralize this
                          // infinite loop for reduced-motion users — framer only strips
                          // the transform shorthands, never a literal `transform` string.
                          animate={{ x: [-100, 170] }}
                          transition={{
                            duration: 0.6,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <div className="w-[20px] h-[6px] rounded-full bg-[var(--dash-accent,var(--primary-500))]"></div>
                          <div className="w-[30px] h-[6px] rounded-full bg-[var(--dash-accent,var(--primary-500))]"></div>
                          <div className="w-[50px] h-[6px] rounded-full bg-[var(--dash-accent,var(--primary-500))]"></div>
                        </motion.div>
                      </div>
                    </div>

                    <div className="flex flex-col items-center gap-4">
                      <SarasIQ size={80} className="w-20 h-16 text-[color:var(--dash-accent,var(--primary-600))] hidden md:inline" />
                      <SarasIQ size={40} className="w-10 h-8 text-[color:var(--dash-accent,var(--primary-600))] md:hidden" />
                      <span className="font-sourcesans italic text-[color:var(--dash-muted,var(--text-medium-emphasis))]">
                        Saras iQ
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center gap-2.5">
                  <div className="block">
                    <h2 className="title md:text-[1.75rem] font-medium font-sourcesans text-[color:var(--dash-ink,var(--text-high-emphasis))]">
                      Connect your pulse backend <br className="md:hidden" /> to power{" "}
                      <span className="text-gradient inline-block relative font-bold text-[1rem] md:text-[28px]">
                        Saras iQ{" "}
                        <StarShape className="absolute -right-3 -top-1 text-lg text-[color:var(--dash-accent,var(--primary-500))]" size={16}/>
                      </span>
                    </h2>
                  </div>
                  <div className="w-full md:w-xl text-[color:var(--dash-muted,var(--text-medium-emphasis))]">
                    Saras iQ will{" "}
                    <span className="font-bold text-[color:var(--dash-ink,var(--text-high-emphasis))]">
                      access your Pulse data sets
                    </span>{" "}
                    and{" "}
                    <span className="font-bold text-[color:var(--dash-ink,var(--text-high-emphasis))]">
                      run seamlessly on top of it
                    </span>
                    , to answer all your data questions—instantly and
                    intelligently
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <Button
                  className="bg-[var(--dash-accent,var(--primary-500))] hover:brightness-95 font-semibold text-[color:var(--dash-band-active-ink,var(--structural-white))] flex items-center gap-2 cursor-pointer"
                  onClick={async () => {
                    try {
                      // First fetch enabled warehouses
                      const { data: enabledWarehousesData }: any =
                        await fetchEnabledWarehouses();
                      let warehouseItem =
                        enabledWarehousesData?._embedded
                          ?.enabledWarehouseList?.[0];

                      // Then create warehouse using the data if needed
                      if (
                        warehouseItem?.name &&
                        warehouseItem?.settings?.projectName
                      ) {
                        await createWarehouse({
                          name: warehouseItem.name,
                          user_id: userDetails?.userId,
                          warehouse_type: "bigquery",
                          config: {
                            project_id: warehouseItem?.settings?.projectName,
                            dataset: warehouseItem?.name,
                          },
                        });
                      } else {
                        const pulseUrl = import.meta.env.VITE_PRODUCT_PULSE;
                        window.open(pulseUrl, "_blank");
                      }
                    } catch (error) {
                      console.error("Failed to create warehouse:", error);
                    }
                  }}
                  disabled={isCreatingWarehouse || isEnabledWarehousesLoading}
                >
                  {isCreatingWarehouse || isEnabledWarehousesLoading ?
                    "Connecting..."
                  : "Connect Pulse Backend"}
                  <ArrowRight className="h-4 w-4 text-base" />
                </Button>
              </div>
            </div>

            {/* Help options at the bottom */}
            <div className="flex flex-col items-center justify-center gap-2 md:gap-5.5 mt-4 md:mt-auto md:absolute md:bottom-11">
              <Button variant="ghost" className="flex items-center gap-2 text-[color:var(--dash-accent,var(--primary-500))] hover:brightness-95 transition-colors cursor-pointer" onClick={() => {window.open(LINKS.TALK_TO_US, "_blank")}}>
                <Headset size={18} className="text-[color:var(--dash-accent,var(--primary-500))]" />
                <span className="text-sm font-medium">Schedule a call</span>
              </Button>
              <Button variant="ghost" className="flex items-center gap-2 text-[color:var(--dash-accent,var(--primary-500))] hover:brightness-95 transition-colors cursor-pointer" onClick={() => {window.open(LINKS.SETTING_UP_PULSE, "_blank")}}>
                <Book size={18} className="text-[color:var(--dash-accent,var(--primary-500))]" />
                <span className="text-sm font-medium">
                  Steps to Set Up Your Pulse Account
                </span>
              </Button>
            </div>
          </>
        }
      </div>
      <div className="absolute bottom-2 left-2 w-[300px]">
        <Card className="align-left p-1">
        <UserAvatar/>
        </Card>
      </div>
    </ProgressContainer>
    
  );
};
